import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useDebounce } from "use-debounce";
import { infiniteScrollHook } from "../../hooks/infiniteScrollHook";
import { ContractorService, ServiceTypesEnum } from "@propertelligent/client-api";
import ContractorTopHeader from "../../newComponents/listings/top-listing-headers/contractor-listing-top-header";
import ContractorCard from "../../newComponents/listings/listing-cards/contractor";
import AddContractorForm from "../../newComponents/contractor/AddContratorForm";
import EmptyState from "../../newComponents/emptystate";
import { GetImages } from "../../utils/GetImages";
import { usePermissions } from "../../hooks/usePermissions";
import { UserLoginData } from "../../components/common/charts/Enums";
import apiHelper from "../../utils/apiHelper";
import { defaultPageSize } from "../../constants/constants";

// ─── Constants ────────────────────────────────────────────────────────────────

const SERVICE_TYPE_MAP: Record<string, number> = {
  Electrician: ServiceTypesEnum.Electrical,
  Plumber: ServiceTypesEnum.Plumbing,
  Exterior: ServiceTypesEnum.Exterior,
  Interior: ServiceTypesEnum.Interior,
  Appliance: ServiceTypesEnum.Appliance,
  Handyman: ServiceTypesEnum.Handyman,
  "General Contractor": ServiceTypesEnum.GeneralContractor,
  Other: ServiceTypesEnum.Other,
};

const sumAggregates = (values: Record<string, number>): number =>
  Object.values(values).reduce((acc, n) => acc + n, 0);

// ─── Hook: base aggregate fetcher ─────────────────────────────────────────────

function useBaseAggregates(
  aggregateValues: Record<string, number> | undefined,
  activeFilter: string,
  debouncedSearchTerm: string
) {
  const [baseAggregateValues, setBaseAggregateValues] = useState<Record<string, number> | null>(null);
  const initialized = useRef(false);

  // Capture the first unfiltered aggregate response as the stable baseline
  useEffect(() => {
    if (
      !initialized.current &&
      aggregateValues &&
      activeFilter === "All" &&
      debouncedSearchTerm === ""
    ) {
      setBaseAggregateValues(aggregateValues);
      initialized.current = true;
    }
  }, [aggregateValues, activeFilter, debouncedSearchTerm]);

  const refreshBaseAggregates = useCallback(async () => {
    const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) ?? "0");
    const tenantId = parseInt(sessionStorage.getItem(UserLoginData.tenant_id) ?? "0");

    try {
      const res = await apiHelper(
        ContractorService.getApiContractorOrganizationlist,
        { showNotification: false },
        orgId || tenantId,
        defaultPageSize,
        1,
        undefined, undefined, undefined, null, null
      );
      if (res?.data?.aggregateValues) {
        setBaseAggregateValues(res.data.aggregateValues);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  return { baseAggregateValues, refreshBaseAggregates };
}

// ─── Hook: computed filter aggregates ─────────────────────────────────────────

function useAggregateValuesFilters(
  aggregateValues: Record<string, number> | undefined,
  baseAggregateValues: Record<string, number> | null,
  activeFilter: string,
  debouncedSearchTerm: string,
  loading: boolean
) {
  const stableRef = useRef<Record<string, number> | undefined>(undefined);

  const computed = useMemo(() => {
    const base = baseAggregateValues ?? aggregateValues;
    if (!base) return undefined;

    const baseCounts = { ...base };
    const hasSearch = debouncedSearchTerm.trim() !== "";

    // When loading, return stable snapshot to prevent count flashes
    if (loading) return stableRef.current;

    if (activeFilter === "All" || hasSearch) {
      return { ...baseCounts, ...(aggregateValues ?? {}) };
    }

    if (aggregateValues && Object.prototype.hasOwnProperty.call(baseCounts, activeFilter)) {
      baseCounts[activeFilter] = Number(aggregateValues[activeFilter] ?? 0);
    }

    return baseCounts;
  }, [activeFilter, aggregateValues, baseAggregateValues, debouncedSearchTerm, loading]);

  // Update stable ref when not loading
  if (!loading && computed) {
    stableRef.current = computed;
  }

  return computed;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ContractorList = () => {
  const { canWrite } = usePermissions();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [isModalOpen, setModalOpen] = useState(false);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const fullyQualifiedFilter = useMemo(() => {
    const filters: string[] = [];

    if (debouncedSearchTerm) {
      filters.push(
        `(User.FirstName + " " + User.LastName).ToLower().Contains("${debouncedSearchTerm.toLowerCase()}")`
      );
    }

    const enumId = SERVICE_TYPE_MAP[activeFilter];
    if (activeFilter !== "All" && enumId) {
      filters.push(`ServiceTypesIds.Any(sid => sid == ${enumId})`);
    }

    return filters.length > 0 ? filters.join(" and ") : null;
  }, [debouncedSearchTerm, activeFilter]);

  const { rows, loading, hasMore, rowCount, fetchData, currentPage, aggregateValues } =
    infiniteScrollHook({
      httpFetchDataFn: ContractorService.getApiContractorOrganizationlist,
      FullyQualifiedFilters: fullyQualifiedFilter,
      pageSize: 10,
      FullyQualifiedSorts: `User.FirstName ${sortDirection}`,
    });

  const { baseAggregateValues, refreshBaseAggregates } = useBaseAggregates(
    aggregateValues,
    activeFilter,
    debouncedSearchTerm
  );

  const aggregateValuesFilters = useAggregateValuesFilters(
    aggregateValues,
    baseAggregateValues,
    activeFilter,
    debouncedSearchTerm,
    loading
  );

  const totalCount = useMemo(() => {
    const source = baseAggregateValues ?? aggregateValues;
    return source ? sumAggregates(source) : 0;
  }, [baseAggregateValues, aggregateValues]);

  const handleFullRefresh = useCallback(() => {
    refreshBaseAggregates();
    fetchData(1);
  }, [fetchData, refreshBaseAggregates]);

  const handleFilterChange = useCallback((filter: string) => {
    if (filter !== activeFilter) setActiveFilter(filter);
  }, [activeFilter]);

  const handleSort = useCallback((dir: "asc" | "desc") => setSortDirection(dir), []);
  const handleOpenModal = useCallback(() => setModalOpen(true), []);
  const handleModalClose = useCallback(() => setModalOpen(false), []);

  const dataHookState = useMemo(
    () => ({ rows, loading, hasMore, fetchData, currentPage, handleFullRefresh }),
    [rows, loading, hasMore, fetchData, currentPage, handleFullRefresh]
  );

  // ─── Derived booleans ───────────────────────────────────────────────────────
  const isEmptyState = rows.length === 0 && !loading;
  const hasAnyContractors = totalCount > 0;
  const showInitialEmptyState = !hasAnyContractors && isEmptyState;
  const hasSearchResults =
    hasAnyContractors && isEmptyState && (searchTerm !== "" || activeFilter !== "All");

  return (
    <div className="content-wrapper container-fluid">
      {showInitialEmptyState ? (
        <div className="my-4 h-100">
          <EmptyState
            icon={GetImages.emptyStateContractor}
            heading="Add Your First Contractor"
            buttonLabel="Add Contractor"
            onButtonClick={handleOpenModal}
            isButton={canWrite("Contractor")}
          />
        </div>
      ) : (
        <>
          {hasAnyContractors && (
            <ContractorTopHeader
              PageHeading={`Contractors (${rowCount})`}
              Count={rowCount}
              ButtonName="Add Contractor"
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              handleSort={handleSort}
              currentSortDirection={sortDirection}
              onClick={handleOpenModal}
              showAddButton={canWrite("Contractor")}
              MobileButtonName="Add"
              activeFilter={activeFilter}
              handleFilterChange={handleFilterChange}
              aggregateValuesFilters={aggregateValuesFilters}
            />
          )}

          {hasSearchResults ? (
            <div className="my-4 h-100">
              <EmptyState
                icon={GetImages.emptyStateContractor}
                heading="No Contractor Found"
                isButton={false}
              />
            </div>
          ) : (
            <ContractorCard
              id="contractors"
              isFooter={true}
              dataHookState={dataHookState}
            />
          )}
        </>
      )}

      {isModalOpen && (
        <AddContractorForm
          isModalOpen={isModalOpen}
          setModalOpen={handleModalClose}
          fetchData={handleFullRefresh}
        />
      )}
    </div>
  );
};

export default React.memo(ContractorList);